import { ChipsPot, ChipsPotSimple } from '@/components/ANewDesign/Board/ChipsLayout'
import { PokerCard } from '@/components/GameAssets/Cards/PokerCard'
import { useTableLayoutPosition } from '@/components/GameAssets/Table/useTableLayout'
import { isWeb } from '@/components/WebWrapper'
import { DS } from '@/design-system'
import { Value } from 'pokersolver'
import { Suit } from 'pokersolver'
import React, { useMemo, useRef, useEffect } from 'react'
import { View, ViewStyle, LayoutChangeEvent, TouchableOpacity } from 'react-native'
import { createComponent } from 'react-native-small-ui'
import { normalizePots, type PotsInput } from './utils/normalizePots'
import { getHighlightedWinnerCards } from './utils/winnerHighlight'
import { useGameContext } from '@/screens/GameContainer/GameContext'
import { useGameQuery, useHandHistoryQuery } from '@/hooks/convex/useGameState'
import { useMeasureOnResize } from '@/hooks/useMeasureOnResize'
import { create } from 'zustand'
import { Board } from '../../components/GameAssets/Table/Table'
import { subscribeWithSelector } from 'zustand/middleware'
import { sidePotSheetRef, useSidePotSheet, useSidePotSheetStore } from './SidePotSheet'
import { sidePotWinnersSheetRef, useSidePotWinnersSheetStore } from './SidePotWinnersSheet'
import { translate } from '../../i18n'
import { Icon } from 'react-native-paper'

const Box = createComponent(View, {
  flexGrow: 1
})
const Column = createComponent(View, {
  flexDirection: 'column'
})
const Row = createComponent(View, {
  flexDirection: 'row'
})
const Center = createComponent(View, {
  alignItems: 'center',
  justifyContent: 'center'
})

type PotLayoutStoreState = { position: { x: number; y: number } }

type PotLayoutStoreActions = {
  setPosition: (nextPosition: PotLayoutStoreState['position']) => void
}

type PotLayoutStore = PotLayoutStoreState & PotLayoutStoreActions

export const usePotLayoutStore = create<PotLayoutStore>()(
  subscribeWithSelector((set) => ({
    position: { x: 0, y: 0 },
    setPosition: (position) => set({ position })
  }))
)

const PotChipDecorations = () => {
  const viewRef = useRef<View>(null)

  // Measure and update position on window resize
  useMeasureOnResize(viewRef, (x, y) => {
    usePotLayoutStore.getState().setPosition({ x, y })
  })

  const onLayout = (e: LayoutChangeEvent) => {
    let target = e.currentTarget
    if (isWeb) {
      // @ts-expect-error
      target = e.nativeEvent.target
    }
    target.measure((_x, _y, width, height, pageX, pageY) => {
      const x = pageX + width * 0.5
      const y = pageY + height * 0.5
      usePotLayoutStore.getState().setPosition({ x, y })
    })
  }

  const chipSize = 24

  return (
    <View ref={viewRef} onLayout={onLayout}>
      <ChipsPotSimple size={chipSize} />
    </View>
  )
}

type PotProps = {
  pots?: PotsInput
  currentRoundBets?: number
  showCurrentBets?: boolean
  containerStyle?: ViewStyle
}

const Pot = (props: PotProps) => {
  if (!props.pots) {
    return null
  }
  const { open } = useSidePotSheet()

  // Normalize pots to array format (handles both new and legacy formats)
  const potsArray = normalizePots(props.pots)
  const hasSidePots = potsArray.length > 1

  // Current round bets (not yet collected)
  const currentRoundBets = props.currentRoundBets ?? 0
  const showCurrentBets = props.showCurrentBets ?? true

  // Total pot from all pots
  const totalPot = potsArray.reduce((sum, pot) => sum + pot.amount, 0)

  // Current bets label text (reused for spacer and visible label)
  const currentBetsText = `(${currentRoundBets})`

  return (
    <Center gap={8} style={props?.containerStyle}>
      <PotChipDecorations />
      <Column gap={4} alignItems="center">
        <Row alignItems="baseline" style={{ alignSelf: 'stretch', justifyContent: 'center' }}>
          {/* Invisible spacer to keep pot centered when current bets are shown */}
          {showCurrentBets && (
            <DS.Text variant="Caption/Regular" color="Brand/Cream" style={{ opacity: 0, marginRight: 4 }}>
              {currentBetsText}
            </DS.Text>
          )}
          <DS.Text variant="Heading 4/Regular" color="Brand/Cream">
            {totalPot}
          </DS.Text>
          {/* Visible current bets indicator */}
          {showCurrentBets && (
            <DS.Text variant="Caption/Regular" color="Brand/Cream" style={{ opacity: 0.7, marginLeft: 4 }}>
              {currentBetsText}
            </DS.Text>
          )}
        </Row>
        <Column gap={2} alignItems="center">
          {hasSidePots && (
            <TouchableOpacity
              onPress={open}
              style={{
                padding: 6,
                paddingVertical: 8
              }}
            >
              <Row>
                <DS.TextScaled variant="Caption/Bold" color="Brand/Cream" style={{ opacity: 0.8 }}>
                  {translate('newGameScreen.common.sidePotsTitle')}
                </DS.TextScaled>
                <Icon source={'chevron-down'} size={16} color={DS.palette['Brand/Cream']} />
              </Row>
            </TouchableOpacity>
          )}
        </Column>
      </Column>
    </Center>
  )
}

const WinnerCards = ({
  cards,
  winningCards,
  containerStyle
}: { cards: string[]; winningCards: string[]; containerStyle?: ViewStyle }) => {
  const card1 = cards?.[0]
  const card2 = cards?.[1]
  const cardWidth = 28

  return (
    <Row gap={2} style={containerStyle}>
      <PokerCard.Flip
        suit={card1[1] as Suit}
        value={card1[0] as Value}
        side={winningCards.includes(card1) ? 'front' : 'back'}
        size={cardWidth}
        soundEnable
        containerStyle={{
          transform: [{ rotate: '-5deg' }]
          // opacity: winningCards.includes(card1) ? 1 : 0.6
        }}
      />
      <PokerCard.Flip
        suit={card2[1] as Suit}
        value={card2[0] as Value}
        side={winningCards.includes(card2) ? 'front' : 'back'}
        size={cardWidth}
        soundEnable
        containerStyle={{
          transform: [{ rotate: '5deg' }]
          // opacity: winningCards.includes(card2) ? 1 : 0.6
        }}
      />
    </Row>
  )
}

export const TableContainer = () => {
  const { gameId, userId } = useGameContext()
  const { game, myPlayer, players } = useGameQuery(gameId, userId)
  const { handHistory } = useHandHistoryQuery(gameId)

  // Calculate current round bets (chips on table not yet collected into pot)
  const currentRoundBets = useMemo(() => {
    if (!players) return 0
    return players.reduce((sum, p) => sum + (p.currentBet ?? 0), 0)
  }, [players])

  const { open: openSidePotSheet } = useSidePotSheet()
  const pauseHandledRef = useRef<number | undefined>(undefined)
  // Auto-open sidepot sheet when game is paused for sidepot inspection (server-side pause)
  // The server pauses the game when a new sidepot is created in scripted tests
  useEffect(() => {
    // @ts-ignore - pausedAt may not be in type
    const pausedAt = game?.pausedAt
    // @ts-ignore
    const isSidepotTest = game?.testType === 'sidepot'
    // Only open sheet for scripted tests when newly paused
    if (isSidepotTest && pausedAt && pausedAt !== pauseHandledRef.current) {
      console.log('[SidepotTest] Game paused by server - opening sidepot sheet')
      pauseHandledRef.current = pausedAt
      // Small delay to let UI settle
      setTimeout(() => {
        openSidePotSheet()
      }, 100)
    }
    // Reset when game is unpaused
    if (!pausedAt) {
      pauseHandledRef.current = undefined
    }
  }, [game?.pausedAt, game?.testType, openSidePotSheet])

  const handOutcome = useMemo(() => {
    if (!(handHistory || game?.handNumber)) {
      return undefined
    }
    return handHistory?.find((h) => h.handNumber === game?.handNumber)
  }, [handHistory, game])

  // Track which hand we've already shown the winners sheet for
  const winnersSheetHandledRef = useRef<string | undefined>(undefined)

  const shouldShowWinnersSheet = useMemo(() => {
    const potWinners = handOutcome?.potDistribution?.potWinners
    const hasMultiplePots = (potWinners?.length ?? 0) > 1
    const hasSplitPot = potWinners?.some((p) => p.winners.length > 1) ?? false
    return hasMultiplePots || hasSplitPot
  }, [handOutcome])

  // Dismiss SidePotSheet the moment a hand ends so it never stacks with the WinnersSheet.
  // Using dismiss() (not close()) because BottomSheetModal.close() only animates to -1
  // without actually removing the modal from the provider stack.
  useEffect(() => {
    if (game?.boardState !== 'hand_complete') return
    sidePotSheetRef.current?.dismiss()
    useSidePotSheetStore.setState({ isOpen: false })
  }, [game?.boardState])

  // Auto-open winners sheet when a hand ends with multiple pots or a split pot.
  // isOpen is set immediately so other sheets (e.g. StartGameSheet) dismiss without delay.
  // The actual present() is deferred slightly to let the UI settle.
  useEffect(() => {
    if (!shouldShowWinnersSheet) return
    if (winnersSheetHandledRef.current === handOutcome?._id) return

    winnersSheetHandledRef.current = handOutcome?._id
    useSidePotWinnersSheetStore.setState({ isOpen: true })
    setTimeout(() => {
      sidePotWinnersSheetRef.current?.present()
    }, 100)
  }, [handOutcome, shouldShowWinnersSheet])

  const winType = useMemo(() => {
    const handRank = handOutcome?.winners?.[0].handRank
    if (!handRank) {
      return
    }
    if (handRank === 'Win by fold') {
      return 'folded'
    }
    return 'showdown'
  }, [handOutcome])

  const winnerCards = useMemo(() => getHighlightedWinnerCards(handOutcome), [handOutcome])

  const renderHeader = useMemo(
    () => (
      <Column flexGrow={1}>
        <Box justifyContent="flex-end" pointerEvents="box-none">
          <Pot pots={game?.pots} currentRoundBets={currentRoundBets} showCurrentBets={game?.showCurrentBets ?? true} />
        </Box>
      </Column>
    ),
    [game?.pots, game?.showCurrentBets, currentRoundBets]
  )
  const renderFooter = useMemo(
    () => (
      <Box
      // backgroundColor={'#0FF9'}
      >
        {game?.boardState === 'hand_complete' && winType === 'showdown' && !shouldShowWinnersSheet && (
          <Center>
            {handOutcome?.winners.map((p) => {
              return (
                <Center key={p.playerId} gap={8}>
                  <DS.Text variant="Heading 5/Medium" color="Global/White">
                    {p.playerName}
                  </DS.Text>
                  <DS.Text variant="Mini/Medium" color="Global/White">
                    {p.handDescription.toUpperCase()}
                  </DS.Text>
                  {winnerCards.length === 1 && <WinnerCards cards={p.cards} winningCards={winnerCards[0]} />}
                </Center>
              )
            })}
          </Center>
        )}
        {game?.boardState === 'hand_complete' && winType === 'folded' && (
          <Center>
            {handOutcome?.winners.slice(0, 1).map((p) => {
              return (
                <Center key={p.playerId} gap={8}>
                  <DS.Text variant="Heading 5/Medium" color="Global/White">
                    {`${p.userId === myPlayer?.userId ? 'You' : p.playerName} won`}
                  </DS.Text>
                  <DS.Text variant="Mini/Medium" color="Global/White">
                    Everyone else folded
                  </DS.Text>
                </Center>
              )
            })}
          </Center>
        )}
      </Box>
    ),
    [game, handOutcome, winnerCards, winType, myPlayer, shouldShowWinnersSheet]
  )

  return (
    <Box>
      <Board headerComponent={renderHeader} footerComponent={renderFooter}>
        <CommunityCards winnerCards={winnerCards[0]} />
      </Board>
    </Box>
  )
}

{
  /* {showPot ? (
            <View ref={ref} onLayout={onLayout} style={[styles.chipsContainer, potContainerStyle]} collapsable={false}>
              {isGameOver ? <HandWinner winnerCardSizes={16} /> : <RoundWinner />}
              {!isGameOver ? (
                <>
                  <ChipsLayout />
                  <DS.Text variant="Heading 4/Regular" color="Brand/Cream" style={{ marginTop: 0 }}>
                    {potText}
                  </DS.Text>
                </>
              ) : null}
            </View>
          ) : (
            <ChipsWaitingGameLayout />
          )} */
}

const CommunityCards = ({ winnerCards = [] }: { winnerCards?: string[] }) => {
  const { gameId, userId } = useGameContext()
  const { game } = useGameQuery(gameId, userId)

  const layout = useTableLayoutPosition()

  // const width = layout.innerTable.width
  const cardWidth = isWeb ? 38 : layout.communityCard.width

  const cards = game?.communityCards
  const maxCards = 5

  return (
    <Box paddingTop={8}>
      {cards && (
        <View
          style={{
            flexDirection: 'row',
            justifyContent: 'center',
            alignItems: 'center',
            flexWrap: 'wrap',
            maxWidth: '100%',
            gap: 4
          }}
        >
          {Array.from({ length: maxCards }).map((_, i, { length }) => {
            const isCardDisabled = cards[i] && winnerCards.length > 0 && !winnerCards.includes(cards[i])
            // first card, second-last and last
            const soundOnIndex = [0, length - 2, length - 1]
            const areCardsEmpty = cards.length === 0
            // If cards are empty only play sound for 1
            const isSoundEnabled = (areCardsEmpty && i === 0) || (!areCardsEmpty && soundOnIndex.includes(i))
            return (
              <PokerCard.Flip
                key={`board-card-flippable-${i}`}
                suit={cards[i]?.[1] as Suit | undefined}
                value={cards[i]?.[0] as Value | undefined}
                side={cards[i] ? 'front' : 'back'}
                soundEnable={isSoundEnabled}
                size={cardWidth}
                containerStyle={{
                  opacity: isCardDisabled ? 0.6 : 1,
                  transform: [
                    {
                      translateY: isCardDisabled ? 4 : 0
                    }
                  ]
                }}
              />
            )
          })}
        </View>
      )}
    </Box>
  )
}
